import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Grid, Paper, Typography, Button, TextField, IconButton, Chip, Avatar, Divider, List, ListItem, ListItemText, ListItemAvatar, Badge, Snackbar, Alert, useTheme } from '@mui/material';
import { Send, AttachFile, Delete, Reply, Forward, MoreVert, Refresh, Search, FilterList, Star, StarBorder, Archive, Inbox, Drafts, Send as SentIcon, Delete as TrashIcon, RestoreFromTrash, Flag, FlagOutlined } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid'; // Para generar IDs únicos
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import EmailSidebar from './EmailSidebar';
import EmailPreview from './EmailPreview';
import MinimizableComposeWindow, { MinimizableComposeWindowProps } from './MinimizableComposeWindow';
import { Email, ComposeContext } from '../../types/email';
import RichTextEditor from '../RichTextEditor';
import EmailList from './EmailList';
import AIEmailGenerator from './AIEmailGenerator';
import { useComposeModals } from '../../contexts/ComposeModalsContext';

// Función temporal para preparar el cuerpo del email para composición
export const prepareBodyForComposition = (
  email: Email, 
  mode: 'reply' | 'reply-all' | 'forward', 
  detalle?: any
): string => {
  if (!email) return '';
  
  // Función helper para sanitizar HTML básico
  const sanitizeHtml = (html: string): string => {
    if (!html) return '';
    
    // Remover scripts y iframes
    let sanitized = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
      .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '');
    
    return sanitized;
  };

  // Función para extraer todos los CIDs del HTML
  const extractCidsFromHtml = (html: string): string[] => {
    if (!html) return [];
    const cidRegex = /src=["']cid:([^"']+)["']/gi;
    const cids: string[] = [];
    let match;
    while ((match = cidRegex.exec(html)) !== null) {
      cids.push(match[1]);
    }
    return cids;
  };

  // Función para reemplazar src="cid:..." por la URL del backend
  const replaceCidSrc = (html: string, email: Email, detalle?: any): string => {
    if (!html || !email) return html;
    
    let newHtml = html;
    const cidsEncontrados = extractCidsFromHtml(html);
    
    if (cidsEncontrados.length > 0) {
      console.log('[DEBUG] CIDs encontrados en HTML:', cidsEncontrados);
      
      // Usar SOLO inline_attachments del detalle
      const attachments = detalle?.inline_attachments || [];
      console.log('[DEBUG] Attachments inline disponibles:', attachments?.map((a: any) => ({id: a.id, name: a.name, contentId: a.contentId})));
      
      // Reemplazar cada cid por su URL correspondiente
      newHtml = newHtml.replace(/src=["']cid:([^"']+)["']/gi, (match, cid) => {
        const cidNorm = cid.replace(/[<>\s]/g, '').toLowerCase();
        
        // Buscar attachment por contentId o por name (normalizados)
        const att = attachments?.find((a: any) => {
          const attContentId = (a.contentId || '').replace(/[<>\s]/g, '').toLowerCase();
          const attName = (a.name || '').replace(/[<>\s]/g, '').toLowerCase();
          return attContentId === cidNorm || attName === cidNorm;
        });
        
        if (att) {
          const backendUrl = `${API_BASE_URL}/email-attachment/${email.id}/${att.id}`;
          return `src="${backendUrl}" style="max-width: 100%; height: auto;"`;
        }
        
        // Si no se encuentra el attachment, reemplazar por mensaje amigable
        return `src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 200' preserveAspectRatio='xMidYMid meet'%3E%3Crect width='100%25' height='100%25' fill='%23f5f5f5' stroke='%23ccc' stroke-width='1'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle' fill='%23888' font-style='italic' font-size='14' font-family='Arial, sans-serif'%3E%3Ctspan x='50%25' dy='-0.5em'%3EImagen pegada%3C/tspan%3E%3Ctspan x='50%25' dy='1.2em'%3Eno disponible%3C/tspan%3E%3C/text%3E%3C/svg%3E" style="max-width: 300px; max-height: 200px; width: auto; height: auto;"`;
      });
    }
    
    return newHtml;
  };

  // Función para procesar el cuerpo del email para visualización
  const processEmailBodyForDisplay = (email: Email, detalle?: any): string => {
    if (!email) return '';
    
    // Obtener el HTML del email
    const html = email.bodyHtml || email.bodyRawHtml || email.body || '';
    
    // Sanitizar y procesar CIDs
    const sanitizedHtml = sanitizeHtml(html);
    const processedHtml = replaceCidSrc(sanitizedHtml, email, detalle);
    
    return processedHtml;
  };
  
  // Procesar el cuerpo original del email
  const originalBody = processEmailBodyForDisplay(email, detalle);
  
  // Crear encabezado de cita
  const formattedDate = new Date(email.receivedDateTime).toLocaleString('es-ES');
  const quoteHeader = `<p><br></p><hr><p><strong>De:</strong> ${email.from?.name} &lt;${email.from?.email}&gt;<br><strong>Enviado:</strong> ${formattedDate}<br><strong>Para:</strong> ${email.to?.map(t => t.email).join(', ')}<br><strong>Asunto:</strong> ${email.subject}</p>`;
  
  // Combinar encabezado con el cuerpo procesado
  return `${quoteHeader}<blockquote>${originalBody}</blockquote>`;
};

// Definir el tipo para una sesión de redacción
interface ComposeSession {
  id: string;
  context: MinimizableComposeWindowProps['initialContext'];
}

const EmailLayout: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const { openComposeModal, goToPacienteEmails, updateCurrentPaciente, currentSelectedPaciente: globalPaciente, updateEmailsLoaded, emailsLoaded, clearSwitchingPatientModal } = useComposeModals();
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [currentFolder, setCurrentFolder] = useState('');
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState<any | null>(null);
  const [emails, setEmails] = useState<any[]>([]);
  const [loadingFolders, setLoadingFolders] = useState<Record<string, boolean>>({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'info' | 'success' | 'warning' | 'error';
  }>({ open: false, message: '', severity: 'info' });
  const [papeleraNeedsUpdate, setPapeleraNeedsUpdate] = useState(false);
  const [papeleraCount, setPapeleraCount] = useState(0);
  const [showMaxWindowsAlert, setShowMaxWindowsAlert] = useState(false);
  const [newEmailsCount, setNewEmailsCount] = useState(0);
  const [folderBadgesUpdateCallback, setFolderBadgesUpdateCallback] = useState<((foldersData: Record<string, number>) => void) | null>(null);
  const folderBadgesUpdateCallbackRef = useRef<((foldersData: Record<string, number>) => void) | null>(null);
  const [isUserDeselecting, setIsUserDeselecting] = useState(false);

  // Leer paciente desde localStorage al cargar
  useEffect(() => {
    const pacienteSeleccionado = localStorage.getItem('selectedPacienteForEmail');
    if (pacienteSeleccionado) {
      try {
        const paciente = JSON.parse(pacienteSeleccionado);
        setSelectedPaciente(paciente);
        // Limpiamos para que no afecte recargas posteriores
        localStorage.removeItem('selectedPacienteForEmail');
      } catch (error) {
        console.error('Error al parsear paciente desde localStorage:', error);
        localStorage.removeItem('selectedPacienteForEmail');
      }
    }
  }, []);

  // Leer paciente desde URL params al cargar
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pacienteId = urlParams.get('pacienteId');
    
    if (pacienteId && !selectedPaciente) {
      // console.log('[DEBUG] EmailLayout - Cargando paciente desde URL params:', pacienteId);
      
      // Buscar el paciente en localStorage (donde normalmente se guardan los pacientes cargados)
      const pacientesGuardados = localStorage.getItem('pacientes');
      if (pacientesGuardados) {
        try {
          const pacientes = JSON.parse(pacientesGuardados);
          const paciente = pacientes.find((p: any) => p.id === pacienteId);
          if (paciente) {
            // console.log('[DEBUG] EmailLayout - Paciente encontrado en localStorage:', paciente);
            setSelectedPaciente(paciente);
            // El flujo normal de EmailSidebar se encargará de abrir el modal de selección de días
          } else {
            // console.log('[DEBUG] EmailLayout - Paciente no encontrado en localStorage, ID:', pacienteId);
          }
        } catch (error) {
          console.error('Error al parsear pacientes desde localStorage:', error);
        }
      }
    }
  }, [selectedPaciente]);

  // === NUEVA FUNCIONALIDAD DE NOTIFICACIONES ===
  // --- LÓGICA DE SONDEO (POLLING) PARA NOTIFICACIONES - VERSIÓN ROBUSTA ---
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingActiveRef = useRef(false);

  // La función de sondeo ahora no depende de nada, es pura.
  const checkForNewEmails = useCallback(async () => {
    // Usamos una función para obtener el estado más reciente sin ser una dependencia.
    let currentPaciente: any = null;
    setSelectedPaciente((p: any) => {
      currentPaciente = p;
      return p;
    });
    
    if (!currentPaciente?.email) {
      return;
    }
    
    if (isPollingActiveRef.current) {
        return;
    }
    isPollingActiveRef.current = true;
    
    try {
      // console.log(`[POLLING] Verificando emails para ${currentPaciente.nombre}...`);
      const response = await axios.get(`${API_BASE_URL}/check-new-emails/${currentPaciente.email}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = response.data;
      
      if (data.has_new && data.count > 0) {
        setNewEmailsCount(prev => prev + data.count);
        
        // Actualizar badges de carpetas si hay datos
        if (data.folders && folderBadgesUpdateCallbackRef.current) {
          folderBadgesUpdateCallbackRef.current(data.folders);
        }
        
        setNotification({
          open: true,
          message: `Hay ${data.count} nuevo(s) mensaje(s) de ${currentPaciente.nombre}`,
          severity: 'info',
        });
      }
    } catch (error) {
      console.error('Error en el sondeo de emails:', error);
      stopPolling();
    } finally {
      isPollingActiveRef.current = false;
    }
  }, [setNewEmailsCount, setNotification]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      // console.log("[POLLING] Sondeo detenido.");
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    checkForNewEmails();
    pollingIntervalRef.current = setInterval(checkForNewEmails, 30000);
    // console.log("[POLLING] Sondeo iniciado.");
  }, [checkForNewEmails, stopPolling]);

  useEffect(() => {
    // Si se selecciona un nuevo paciente, detener el sondeo anterior.
    stopPolling();
    // Cuando cambiamos de paciente, reseteamos el contador de notificaciones.
    setNewEmailsCount(0);
  }, [selectedPaciente?.id, stopPolling]);

  useEffect(() => {
    // Si estamos en la pantalla de email, el paciente está seleccionado y los emails están cargados.
    if (location.pathname === '/email' && selectedPaciente?.email && emailsLoaded) {
      startPolling();
    } else {
      // Si no se cumple alguna condición, nos aseguramos de que el sondeo esté detenido.
      stopPolling();
    }

    // La función de limpieza se asegura de detener el sondeo cuando el componente se desmonte
    // o cuando cambien las dependencias (el paciente o la ruta).
    return () => {
      stopPolling();
    };
  }, [selectedPaciente?.email, emailsLoaded, location.pathname, startPolling, stopPolling]);

  const updateEmailFlag = (emailId: string, flagged: boolean) => {
    setEmails(prev => {
      const updated = prev.map(email =>
        email.id === emailId
          ? { ...email, isFlagged: flagged, isImportant: flagged }
          : email
      );
      if (selectedEmail && selectedEmail.id === emailId) {
        setSelectedEmail({ ...selectedEmail, isFlagged: flagged, isImportant: flagged });
      }
      console.debug('[DEBUG] Emails después de updateEmailFlag:', updated);
      return updated;
    });
  };

  const handleDeleteEmail = async (emailId: string) => {
    const emailToDelete = emails.find(e => e.id === emailId);
    if (!emailToDelete) return;

    const originalEmails = [...emails]; // Guardar estado original para un posible rollback.
    const isTrashLoaded = originalEmails.some(e => e.folder === 'papelera');

    // --- Actualización Optimista Inteligente ---
    if (isTrashLoaded) {
      // CASO 1: La papelera ya está cargada.
      // Movemos el email a la papelera dentro del estado, sin recargar.
      setEmails(prevEmails => {
        const emailMovido = { ...emailToDelete, folder: 'papelera' };
        const otrosEmails = prevEmails.filter(e => e.id !== emailId);
        // Ponemos el email recién "borrado" al principio de la lista para que sea visible.
        return [emailMovido, ...otrosEmails];
      });
    } else {
      // CASO 2: La papelera no está cargada.
      // Simplemente quitamos el email y actualizamos el contador.
      setEmails(prevEmails => prevEmails.filter(e => e.id !== emailId));
      setPapeleraNeedsUpdate(true);
      setPapeleraCount(prev => prev + 1);
    }

    if (selectedEmail?.id === emailId) {
      setSelectedEmail(null);
    }

    try {
      // Llamada a la API en segundo plano.
      await axios.post(`${API_BASE_URL}/delete-email`, { message_id: emailId });
      // Si tiene éxito, no hacemos nada. La UI ya está actualizada.
    } catch (error) {
      console.error('Error al eliminar el email:', error);
      // Si la API falla, revertimos TODOS los cambios en la UI al estado original.
      setEmails(originalEmails);
      if (!isTrashLoaded) {
        // Si tocamos los contadores, también los revertimos.
        setPapeleraNeedsUpdate(false);
        setPapeleraCount(prev => prev - 1);
      }
    }
  };

  const handleRestoreEmail = async (emailId: string) => {
    const emailToRestore = emails.find(e => e.id === emailId);
    if (!emailToRestore) return;

    // Actualización optimista: el email desaparece de la papelera al instante.
    setEmails(prev => prev.filter(email => email.id !== emailId));
    if (selectedEmail?.id === emailId) {
      setSelectedEmail(null);
    }
    
    // Decrementar contador optimista de papelera
    setPapeleraCount(prev => Math.max(0, prev - 1));

    try {
      await axios.post(`${API_BASE_URL}/restore-email`, { message_id: emailId });
      // Disparamos el refresco "inteligente" para que el email aparezca en su carpeta de origen.
      setRefreshTrigger(prev => prev + 1);
    } catch (error)
     {
      console.error('Error al restaurar el email:', error);
      // Si falla la API, lo devolvemos a la lista y revertimos el contador.
      setEmails(prev => [...prev, emailToRestore].sort((a, b) => new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime()));
      setPapeleraCount(prev => prev + 1);
    }
  };

  const handleEmailSelect = (email: Email) => {
    // No hacer nada si el email ya está seleccionado
    if (selectedEmail?.id === email?.id) {
      return;
    }
    setSelectedEmail(email);
  };

  const handleSetFullEmail = (fullEmail: Email) => {
    setSelectedEmail(fullEmail);
    // También actualizar el email en la lista de emails
    setEmails(prevEmails => 
      prevEmails.map(email => 
        email.id === fullEmail.id ? fullEmail : email
      )
    );
  };

  // Función para actualizar el estado de lectura de un email en la lista
  const updateEmailReadStatus = (emailId: string, isRead: boolean) => {
    setEmails(prevEmails => 
      prevEmails.map(email => 
        email.id === emailId ? { ...email, isRead } : email
      )
    );
    // También actualizar el email seleccionado si es el mismo
    if (selectedEmail?.id === emailId) {
      setSelectedEmail(prev => prev ? { ...prev, isRead } : null);
    }
  };

  const handleCompose = (context: ComposeContext) => {
    const result = openComposeModal(context);
    if (result === null) {
      setShowMaxWindowsAlert(true);
    }
  };

  const handleLegacyComposeClick = (mode: 'new' | 'reply' | 'reply-all' | 'forward', email: Email | null) => {
    handleCompose({ mode, email });
  };

  const handleComposeClose = (id: string) => {
    // Ya no necesitamos manejar esto localmente
  };

  const handleSendSuccess = () => {
    // Refrescar la lista de emails
    setRefreshTrigger(prev => prev + 1);
  };

  const handleFolderBadgesUpdate = (callback: (foldersData: Record<string, number>) => void) => {
    folderBadgesUpdateCallbackRef.current = callback;
  };

  // Sincronizar paciente seleccionado con el contexto global
  useEffect(() => {
    // console.log('[DEBUG] EmailLayout - Sincronizando paciente con contexto global:', {
    //   selectedPaciente: selectedPaciente?.id,
    //   selectedPacienteName: selectedPaciente?.nombre
    // });
    updateCurrentPaciente(selectedPaciente);
  }, [selectedPaciente, updateCurrentPaciente]);

  // Sincronizar desde el contexto global hacia EmailLayout
  useEffect(() => {
    // Solo sincronizar si hay un paciente válido en el contexto global
    // y no hay paciente seleccionado localmente, o si son diferentes
    // PERO NO sincronizar si el usuario acaba de deseleccionar el paciente
    if (globalPaciente && !isUserDeselecting && (!selectedPaciente || selectedPaciente.id !== globalPaciente.id)) {
      // console.log('[DEBUG] EmailLayout - Sincronizando desde contexto global:', {
      //   globalPaciente: globalPaciente?.id,
      //   selectedPaciente: selectedPaciente?.id,
      //   isUserDeselecting
      // });
      setSelectedPaciente(globalPaciente);
    }
    // NO sincronizar cuando:
    // - globalPaciente es null y selectedPaciente es null (ambos deseleccionados)
    // - isUserDeselecting es true (usuario deseleccionó el paciente)
  }, [globalPaciente, selectedPaciente, isUserDeselecting]);



  return (
    <Box sx={{ 
      display: 'flex', 
      backgroundColor: 'white'
    }}>
      {/* Columna izquierda: Navegación + Lista de emails */}
      <Box sx={{ 
        width: 400, 
        borderRight: 1, 
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <EmailSidebar
          refreshTrigger={refreshTrigger} // <-- 3. Pasa el gatillo al sidebar
          currentFolder={currentFolder}
          onFolderChange={setCurrentFolder}
          onComposeClick={handleLegacyComposeClick}
          onEmailSelect={handleEmailSelect}
          selectedEmail={selectedEmail}
          setLoadingEmails={setLoadingEmails}
          loadingEmails={loadingEmails}
          selectedPaciente={selectedPaciente}
          setSelectedPaciente={(paciente) => {
            // Marcar si es una deselección del usuario
            if (!paciente) {
              setIsUserDeselecting(true);
              // Resetear el flag después de un breve delay
              setTimeout(() => setIsUserDeselecting(false), 100);
            }
            

            
            setSelectedPaciente(paciente);
            if (!paciente) {
              setSelectedEmail(null);
              setCurrentFolder(''); // Deseleccionar carpeta cuando se borra el paciente
            }
          }}
          emails={emails}
          setEmails={setEmails}
          updateEmailFlag={updateEmailFlag}
          onRestoreEmail={handleRestoreEmail}
          onDeleteEmail={handleDeleteEmail}
          loadingFolders={loadingFolders}
          setLoadingFolders={setLoadingFolders}
          papeleraNeedsUpdate={papeleraNeedsUpdate}
          setPapeleraNeedsUpdate={setPapeleraNeedsUpdate}
          papeleraCount={papeleraCount}
          setPapeleraCount={setPapeleraCount}
          newEmailsCount={newEmailsCount}
          setNewEmailsCount={setNewEmailsCount}
          updateEmailsLoaded={updateEmailsLoaded}
          updateFolderBadges={handleFolderBadgesUpdate}
          emailsLoaded={emailsLoaded}
          clearSwitchingPatientModal={clearSwitchingPatientModal}
        />
      </Box>

      {/* Columna derecha: Vista previa del email */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <EmailPreview 
          email={selectedEmail}
          onCompose={handleCompose}
          onSetFullEmail={handleSetFullEmail}
          loading={loadingEmails}
          paciente={selectedPaciente}
          updateEmailFlag={updateEmailFlag}
          updateEmailReadStatus={updateEmailReadStatus}
          onDeleteEmail={handleDeleteEmail}
          onRestoreEmail={handleRestoreEmail}
          currentFolder={currentFolder}
        />
      </Box>

      {/* Snackbar para alerta de límite de ventanas */}
      <Snackbar
        open={showMaxWindowsAlert}
        autoHideDuration={4000}
        onClose={() => setShowMaxWindowsAlert(false)}
        message="Máximo 3 ventanas de composición permitidas. Cierra una ventana antes de abrir otra."
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      {/* Snackbar para notificaciones de emails nuevos */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, open: false }))} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailLayout; 